import { Cancel as CancelIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { Alert, Box, Chip, Typography } from '@mui/material';
import {
  BooleanField,
  Datagrid,
  FunctionField,
  Labeled,
  ReferenceArrayField,
  Show,
  SimpleShowLayout,
  TextField,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import { CommandCategoryIcon, CommandIconField, DangerLevelBadge } from '../../components/commands';
import { Command } from '../../types/commands';
import { CommandRestoreButton } from './CommandRestoreButton';

/**
 * `GET /v3/api/commands/{id}` resolves soft-deleted commands (the route is
 * registered with `withTrashed(['show'])`), so a greyed-out row in the list opens
 * here and can be restored from this notice.
 */
/** `label={false}` keeps SimpleShowLayout from adding an empty field label above it. */
const DeletedCommandNotice = (_props: { label?: boolean }) => {
  const record = useRecordContext<Command>();
  const translate = useTranslate();

  if (!record?.deleted_at) return null;

  return (
    <Alert severity="warning" action={<CommandRestoreButton variant="contained" />} sx={{ mb: 1 }}>
      {translate('resources.commands.restore.deleted_notice', { date: record.deleted_at })}
    </Alert>
  );
};

export const CommandShow = () => {
  return (
    <Show>
      <SimpleShowLayout>
        <DeletedCommandNotice label={false} />
        <FunctionField
          label="Status"
          render={(record: Command) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {record.is_active ? (
                <>
                  <Chip label="Active" color="success" size="small" icon={<CheckCircleIcon color="success" />} />
                </>
              ) : (
                <>
                  <Chip label="Inactive" color="error" size="small" icon={<CancelIcon color="error" />} />
                </>
              )}
            </Box>
          )}
        />
        <TextField source="name" />
        <TextField source="display_name" label="Display Name" />
        <TextField source="description" />
        <FunctionField
          label="Category"
          render={(record: Command) => {
            const label = record.category
              ? record.category.charAt(0).toUpperCase() + record.category.slice(1)
              : 'Unknown';
            return record.category ? (
              <Chip
                label={label}
                size="medium"
                icon={<CommandCategoryIcon category={record.category} size="medium" />}
              />
            ) : null;
          }}
        />
        <FunctionField
          label="Danger Level"
          render={(record: Command) =>
            record.danger_level ? <DangerLevelBadge level={record.danger_level} size="medium" /> : null
          }
        />
        <FunctionField
          label="Icon"
          render={(record: Command) => (record.icon ? <CommandIconField icon={record.icon} size="medium" /> : null)}
        />
        <TextField source="mqtt_topic_pattern" label="MQTT Topic Pattern" />
        <BooleanField source="confirmation_required" label="Requires Confirmation" />
        <ParameterListField />
      </SimpleShowLayout>
    </Show>
  );
};

const ParameterListField = () => {
  const record = useRecordContext<Command>();
  if (!record?.parameter_ids || record.parameter_ids.length === 0) {
    return (
      <Labeled label="Parameters">
        <Typography component="span" variant="body2">
          No parameters defined
        </Typography>
      </Labeled>
    );
  }
  return (
    <ReferenceArrayField
      source="parameter_ids"
      label="Parameters"
      emptyText="No parameters defined"
      reference={`commands/${record?.id}/parameters`}
    >
      <Datagrid rowClick={false} bulkActionButtons={false}>
        <TextField source="name" />
        <TextField source="display_name" label="Display Name" />
        <BooleanField source="is_required" label="Required" />
        <TextField source="description" />
      </Datagrid>
    </ReferenceArrayField>
  );
};
